/** Display formatting. Everything is stored UTC; the UI shows local time. */

const KO = "ko-KR";

export function formatNumber(value: number): string {
  return new Intl.NumberFormat(KO).format(Math.round(value));
}

/** 1.2만 / 3.4천 — compact enough for a KPI card. */
export function formatCompact(value: number): string {
  return new Intl.NumberFormat(KO, { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

// Sub-cent costs keep significant digits, not a fixed number of places: six
// places still print $0.000000 for anything cheaper than a micro-dollar, and a
// cheap model billed per million tokens lands there routinely.
const SUB_CENT = new Intl.NumberFormat(KO, { maximumSignificantDigits: 4 });

/**
 * Cost arrives as a decimal string. Sub-cent totals still matter when you're
 * comparing models, so they keep more places instead of rounding to $0.00.
 */
export function formatUsd(value: string | number): string {
  const amount = typeof value === "string" ? Number.parseFloat(value) : value;
  if (!Number.isFinite(amount)) return "$0.00";
  if (amount === 0) return "$0.00";
  // Significant digits also drop the float noise a summed column carries in.
  if (Math.abs(amount) < 0.01) return `$${SUB_CENT.format(amount)}`;
  if (Math.abs(amount) < 1) return `$${amount.toFixed(4)}`;
  return `$${new Intl.NumberFormat(KO, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)}`;
}

export function formatPercent(ratio: number, digits = 1): string {
  if (!Number.isFinite(ratio)) return "0%";
  return `${(ratio * 100).toFixed(digits)}%`;
}

export function formatLatency(ms: number): string {
  if (!Number.isFinite(ms)) return "-";
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)}초` : `${Math.round(ms)}ms`;
}

/** UTC instant -> local wall clock. */
export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(KO, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}

/** Axis tick: hourly buckets show the hour, daily buckets the date. */
export function formatBucket(iso: string, interval: "hour" | "day"): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return interval === "hour"
    ? new Intl.DateTimeFormat(KO, { month: "2-digit", day: "2-digit", hour: "2-digit", hour12: false }).format(date)
    : new Intl.DateTimeFormat(KO, { month: "2-digit", day: "2-digit" }).format(date);
}

/** Signed change vs the previous window, or null when there's nothing to compare. */
export function delta(current: number, previous: number | undefined): number | null {
  if (previous === undefined || previous === 0) return null;
  return (current - previous) / previous;
}

export function formatDelta(ratio: number | null): string {
  if (ratio === null) return "";
  const sign = ratio > 0 ? "+" : "";
  return `${sign}${(ratio * 100).toFixed(1)}%`;
}

/** Deterministic series color from the Wanted accent tokens — same key, same color. */
const SERIES_TOKENS = [
  "var(--primary-normal)",
  "var(--accent-foreground-violet)",
  "var(--accent-foreground-cyan)",
  "var(--accent-foreground-orange)",
  "var(--accent-foreground-green)",
  "var(--accent-foreground-purple)",
  "var(--accent-foreground-lime)",
  "var(--accent-foreground-red)",
];

export function seriesColor(key: string, index?: number): string {
  if (typeof index === "number") return SERIES_TOKENS[index % SERIES_TOKENS.length];
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return SERIES_TOKENS[hash % SERIES_TOKENS.length];
}
